import { useCallback } from 'react';
import { BaseTimePeriod, TimePeriod } from '@services/FiltersTypes';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import getComparisonPeriod, { PeriodFormat } from '@components/filter-selection-note/GetComparisonPeriod';
import { checkPeriodicity } from '@components/filtered-period/FilteredPeriod';
import { Periodicity, PeriodicityKey } from '@components/time-period-selector/PeriodsTypes';
import TimeFramePicker from '../../TimeFramePicker';
import {
  hasMATOrYTDPeriodicity,
  isValidPageToDisplayPeriod,
  MAT_YTD_PREFIX_LENGTH,
  periodOptionsCallback,
} from './WrapperUtils';
import { Option, PageName } from '@utils/pageConfig/Types';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { CustomMonthsPeriodState, CustomWeeksPeriodState } from '@state/reducers/main-filter/MainFiltersReducer';

export interface TFPWrapperProps {
  currentPeriodicity: Periodicity;
  currentPeriod: BaseTimePeriod;
  currentComparison: RelativePeriod | BaseTimePeriod;
  periodOptions: TimePeriod;
  applyPickerCallback: (
    periodicity: Periodicity,
    period: BaseTimePeriod,
    relativePeriod: RelativePeriod | BaseTimePeriod,
  ) => void;
  isTimeFramePickerDisabled: boolean;
  isPeriodOptionsDisabled: boolean;
  disabledPeriodicityOptions?: Periodicity[];
  periodFormat?: PeriodFormat;
  enableComparisonPeriodCheck?: boolean;
  customMonthsPeriod?: CustomMonthsPeriodState;
  customWeeksPeriod?: CustomWeeksPeriodState;
  appliedCountries?: Option[];
  appliedProductGroups?: Option[];
  isPeriodOptionsDisabledPredicate?: (periodicity: Periodicity) => boolean;
  isHideComparisonPeriodsPanelPredicate?: (period: Periodicity) => boolean;
  isDisplayNoComparisonLabel?: boolean;
}

const TimeFramePickerWrapper: React.FC<TFPWrapperProps> = ({
  currentPeriodicity,
  currentPeriod,
  currentComparison,
  periodOptions,
  applyPickerCallback,
  isTimeFramePickerDisabled,
  isPeriodOptionsDisabled,
  disabledPeriodicityOptions,
  periodFormat,
  enableComparisonPeriodCheck,
  customMonthsPeriod,
  customWeeksPeriod,
  appliedCountries,
  appliedProductGroups,
  isPeriodOptionsDisabledPredicate,
  isHideComparisonPeriodsPanelPredicate,
  isDisplayNoComparisonLabel,
}) => {
  const handleApplyPicker = (
    periodicity: Periodicity,
    period: BaseTimePeriod,
    relativePeriod: RelativePeriod | BaseTimePeriod,
  ) => {
    if (!isPeriodicityMatOrYtd(periodicity)) {
      applyPickerCallback(periodicity, period, relativePeriod);
      return;
    }

    // transform
    const prefix = Periodicity.MAT === periodicity ? 'MAT ' : 'YTD ';
    const newPeriodicity = period.label.includes('Week') ? Periodicity.Weekly : Periodicity.Monthly;
    const newLabel = prefix + period.label;
    applyPickerCallback(newPeriodicity, { ...period, label: newLabel }, relativePeriod);
  };

  const currentPageName = usePageConfig().pageName;
  const newCurrentPeriodicity = transformPeriodicity(currentPeriodicity, currentPeriod, currentPageName);

  const { earliestPeriod, latestPeriod } = getLatestAndComparisonPeriod(
    currentPeriod,
    currentPeriodicity,
    currentComparison,
    periodFormat,
  );

  // Construct the available periodicity options based on different retailer options
  const periodicityOptions: Periodicity[] = constructPeriodicityOptions(periodOptions, currentPageName);
  // Memoised the callback function to prevent unnecessary renders on rerender when invoked by
  // child components in TimeFramePicker
  const periodOptionsCallbackMemo = useCallback(periodOptionsCallback(periodOptions, currentPageName), [
    periodOptions,
    currentPageName,
  ]);

  return (
    <TimeFramePicker
      currentPeriodicity={newCurrentPeriodicity}
      currentComparison={currentComparison}
      currentEarliestPeriod={earliestPeriod}
      currentLatestPeriod={latestPeriod}
      periodicityOptions={periodicityOptions}
      periodOptionsCallback={periodOptionsCallbackMemo}
      applyPickerCallback={handleApplyPicker}
      isTimeFramePickerDisabled={isTimeFramePickerDisabled}
      isPeriodOptionsDisabled={isPeriodOptionsDisabled}
      disabledPeriodicityOptions={disabledPeriodicityOptions}
      enableComparisonPeriodCheck={enableComparisonPeriodCheck}
      customMonthsPeriod={customMonthsPeriod}
      customWeeksPeriod={customWeeksPeriod}
      appliedCountries={appliedCountries}
      appliedProductGroups={appliedProductGroups}
      isPeriodOptionsDisabledPredicate={isPeriodOptionsDisabledPredicate}
      isHideComparisonPeriodsPanelPredicate={isHideComparisonPeriodsPanelPredicate}
      isDisplayNoComparisonLabel={isDisplayNoComparisonLabel}
    />
  );
};

const isPeriodicityMatOrYtd = (p: Periodicity) => {
  return Periodicity.MAT === p || Periodicity.YTD === p;
};

const isLabelMatOrYtd = (period: BaseTimePeriod) => {
  return period.label.includes(PeriodicityKey.YTD) || period.label.includes(PeriodicityKey.MAT);
};

const transformPeriodicity = (periodicity: Periodicity, period: BaseTimePeriod, pageName: PageName): Periodicity => {
  if (!period) {
    return periodicity;
  }

  if (period.label.includes(PeriodicityKey.YTD)) {
    return Periodicity.YTD;
  }

  if (period.label.includes(PeriodicityKey.MAT)) {
    return Periodicity.MAT;
  }

  if (!isValidPageToDisplayPeriod(pageName) && periodicity === Periodicity.RollingQuarter) {
    return Periodicity.Monthly;
  }

  return periodicity;
};

/**
 * The BaseTimePeriod values returned from this function should never be saved
 * into Redux or Context. Its to be used to format the display description in
 * the TimeFramePicker dropdown display.
 */
const getLatestAndComparisonPeriod: (
  period: BaseTimePeriod,
  periodicity: Periodicity,
  comparison: RelativePeriod | BaseTimePeriod,
  periodFormat?: PeriodFormat,
) => { earliestPeriod: BaseTimePeriod; latestPeriod: BaseTimePeriod } = (
  period,
  periodicity,
  comparison,
  periodFormat,
) => {
  let selectedPeriodStr: string = period?.label || '';
  let comparisonPeriod: string = getComparisonPeriod(period, periodicity, comparison, periodFormat);

  if (!period || period.value === '') {
    return {
      earliestPeriod: { label: '', value: '', lastUpdatedDate: 0 },
      latestPeriod: { label: '', value: '', lastUpdatedDate: 0 },
    };
  }

  if (isLabelMatOrYtd(period)) {
    comparisonPeriod = checkPeriodicity(period.label);
    selectedPeriodStr = period.label.slice(MAT_YTD_PREFIX_LENGTH);
    return {
      earliestPeriod: {
        ...period,
        label: comparisonPeriod,
      },
      latestPeriod: {
        ...period,
        label: selectedPeriodStr,
      },
    };
  }

  // Append weekly prefix to the derived comparison period
  if (periodicity === Periodicity.Weekly) {
    comparisonPeriod = `Week ${comparisonPeriod}`;
  }

  return {
    earliestPeriod: {
      ...period,
      label: comparisonPeriod,
      value: comparisonPeriod,
    },
    latestPeriod: {
      ...period,
      label: selectedPeriodStr,
      value: selectedPeriodStr,
    },
  };
};
const CUSTOM_MONTHS_PAGES = [
  PageName.PERFORMANCE_ANALYSIS,
  PageName.OPPORTUNITY_REVIEW,
  PageName.CATEGORY_LEADERBOARD,
  PageName.EXPLORE,
  PageName.TRADING_ASSISTANT,
];

const CUSTOM_WEEKS_PAGES = [PageName.EXPLORE, PageName.OPPORTUNITY_REVIEW, PageName.TRADING_ASSISTANT];

/**
 * Conditionally add the periodicity options for selection if available.
 */
const constructPeriodicityOptions: (periodOptions: TimePeriod, pageName: PageName) => Periodicity[] = (
  periodOptions,
  pageName,
) => {
  const periodicityOptions: Periodicity[] = [];

  if (periodOptions?.weekly?.length > 0) {
    periodicityOptions.push(Periodicity.Weekly);
  }

  if (periodOptions?.monthly?.length > 0) {
    periodicityOptions.push(Periodicity.Monthly);
  }

  if (periodOptions?.rollingQuarter?.length > 0 && isValidPageToDisplayPeriod(pageName)) {
    periodicityOptions.push(Periodicity.RollingQuarter);
  }

  if (periodOptions?.calendarQuarter?.length > 0 && isValidPageToDisplayPeriod(pageName)) {
    periodicityOptions.push(Periodicity.CalendarQuarter);
  }
  if (periodOptions?.customMonths?.length > 0 && CUSTOM_MONTHS_PAGES.includes(pageName)) {
    periodicityOptions.push(Periodicity.CustomMonths);
  }
  if (periodOptions?.customWeeks?.length > 0 && CUSTOM_WEEKS_PAGES.includes(pageName)) {
    periodicityOptions.push(Periodicity.CustomWeeks);
  }
  /*RO page do not have YTD and MAT*/
  if (pageName === PageName.RETAILER_OVERVIEW) {
    return periodicityOptions;
  }
  const isYTDExist = hasMATOrYTDPeriodicity(periodOptions, Periodicity.YTD);
  if (isYTDExist) {
    periodicityOptions.push(Periodicity.YTD);
  }

  const isMATExist = hasMATOrYTDPeriodicity(periodOptions, Periodicity.MAT);
  if (isMATExist) {
    periodicityOptions.push(Periodicity.MAT);
  }

  return periodicityOptions;
};

export default TimeFramePickerWrapper;
